import { AxiosError } from "axios";
import { showToast } from "./toast";
export const baseURL: string = import.meta.env.VITE_SERVER_BASE_URL;

/**
 *
 * @param dateString - in this format: 2023-06-16T15:50:27.596Z
 * @param returnIsToday - when true, it returns date - in this format: "8:58 PM, Today", else returns default format
 * @returns date - in this format: 8:58 PM, Jun 7, 2023
 */
export function formatDate(dateString: string, returnIsToday: boolean = true) {
  if (!dateString) return "";

  const date = new Date(dateString);

  const options: Intl.DateTimeFormatOptions = {
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  };

  const time = date.toLocaleTimeString("en-US", options);

  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();

  if (isToday && returnIsToday) {
    return `${time}, Today`;
  } else {
    const options: Intl.DateTimeFormatOptions = {
      month: "short",
      day: "numeric",
      year: "numeric",
    };
    const formattedDate = date.toLocaleDateString("en-US", options);
    return `${time}, ${formattedDate}`;
  }
}

export function capitalizeWords(inputString: string): string {
  const words = inputString.toLowerCase().split(" ");
  const capitalizedWords = words.map((word) => {
    if (!word.length) return;

    return word.charAt(0).toUpperCase() + word.slice(1);
  });

  return capitalizedWords.join(" ");
}

export async function copyToClipboard(str: string, cb?: () => void) {
  try {
    await navigator.clipboard.writeText(str);
    cb?.();
  } catch (err) {
    console.error("Failed to copy:", err);
  }
}

//format number with comma function
export function formatNumberWithComma(value: string) {
  const cleanedText = value.replace(/[^0-9]/g, "");
  const formattedText = cleanedText.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return formattedText;
}

export enum AppRoutes {
  HOME = "/",
  DASHBOARD = "/dashboard",
  LOGIN = "/login",
}

export const arrayOrEmpty = <T>(data: unknown): T[] => {
  return Array.isArray(data) ? (data as T[]) : [];
};

/**
 * Match a pathname against a list of route definitions, supporting
 * `:param` segments. Used for page titles, breadcrumbs, and route guards.
 */
export function matchRouteByPathname<T extends { path: string }>(
  routeList: T[],
  pathname: string,
): T | undefined {
  return routeList.find((route) => {
    const routeParts = route.path.split("/").filter(Boolean);
    const pathnameParts = pathname.split("/").filter(Boolean);

    if (routeParts.length !== pathnameParts.length) {
      return false;
    }

    return routeParts.every(
      (part, index) => part === pathnameParts[index] || part.startsWith(":"),
    );
  });
}

export const getRandomColor = (): string => {
  const r = Math.floor(150 + Math.random() * 105);
  const g = Math.floor(150 + Math.random() * 105);
  const b = Math.floor(150 + Math.random() * 105);
  return `rgb(${r}, ${g}, ${b})`;
};

export function getInitials(accountName: string) {
  //remove "PAZZPORT/" prefix if it exists
  const cleanedName = accountName.replace(/^PAZZPORT\//i, "").trim();
  const parts = cleanedName.split(" ").filter(Boolean);

  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0]?.charAt(0).toUpperCase() ?? "";

  const first = parts[0]?.charAt(0) ?? "";
  const last = parts[parts.length - 1]?.charAt(0) ?? "";
  return `${first}${last}`.toUpperCase();
}

export function stringToColor(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  // Generate color in HSL format for better distribution and contrast
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 60%, 60%)`;
}

export function parseError(error: unknown): string {
  // Check AxiosError first since it extends Error
  if (error instanceof AxiosError) {
    return error?.response?.data?.message || error.message || "Request failed";
  }

  if (error instanceof Error) {
    return error.message;
  }

  return "An unknown error occurred";
}

export const handleApiResponseError = (error: unknown) => {
  if (error instanceof AxiosError) {
    showToast(error.response?.data.message, "error");
    return;
  }

  if (error instanceof Error) {
    showToast(error.message, "error");
    return;
  }
};
